/**
 * Reports page - main page for portfolio analytics and reports
 */

import { AssetValueChart } from '@/components/charts/AssetValueChart'
import { ProfitLossChart } from '@/components/charts/ProfitLossChart'
import { SectionErrorBoundary } from '@/components/common/SectionErrorBoundary'
import { DateRangeFilter } from '@/components/reports/DateRangeFilter'
import { PortfolioSummary } from '@/components/reports/PortfolioSummary'
import { useAssets } from '@/services/api/assets'
import { useGoldPrices } from '@/services/api/goldPrice'
import { usePriceHistory } from '@/services/api/prices'
import { useSales } from '@/services/api/sales'
import { getFirstDayOfMonth, getLastDayOfMonth } from '@/utils/dateUtils'
import { useMemo, useState } from 'react'

export default function Reports() {
  const [startDate, setStartDate] = useState(getFirstDayOfMonth())
  const [endDate, setEndDate] = useState(getLastDayOfMonth())

  const { data: assets = [] } = useAssets()
  const { data: sales = [] } = useSales()
  const { data: priceHistory = [] } = usePriceHistory()
  const { data: currentPrices = [] } = useGoldPrices('Hồ Chí Minh')

  // Create map of latest prices by gold type ID
  // Convert GoldType to GoldPriceHistory format for PortfolioSummary
  const latestPrices = useMemo(() => {
    const priceMap = new Map<number, import('@/types/price').GoldPriceHistory>()
    const now = new Date().toISOString()
    const today = new Date().toISOString().split('T')[0] as string // YYYY-MM-DD format

    currentPrices.forEach(price => {
      priceMap.set(price.Id, {
        id: `current_${price.Id}`,
        date: today,
        goldTypeId: price.Id,
        branchName: price.BranchName,
        buyPrice: price.BuyValue,
        sellPrice: price.SellValue,
        createdAt: now,
      })
    })
    return priceMap
  }, [currentPrices])

  const handleReset = () => {
    setStartDate(getFirstDayOfMonth())
    setEndDate(getLastDayOfMonth())
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 lg:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-6 sm:mb-8">
        Báo cáo & Phân tích
      </h1>

      <DateRangeFilter
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onReset={handleReset}
      />

      <SectionErrorBoundary sectionName="Tóm tắt danh mục">
        <div className="mb-6">
          <PortfolioSummary assets={assets} sales={sales} latestPrices={latestPrices} />
        </div>
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Biểu đồ giá trị tài sản">
        <div className="mb-6">
          <AssetValueChart
            assets={assets}
            sales={sales}
            priceHistory={priceHistory}
            {...(startDate ? { startDate } : {})}
            {...(endDate ? { endDate } : {})}
          />
        </div>
      </SectionErrorBoundary>

      <SectionErrorBoundary sectionName="Biểu đồ lợi nhuận/lỗ">
        <div className="mb-6">
          <ProfitLossChart
            assets={assets}
            sales={sales}
            priceHistory={priceHistory}
            {...(startDate ? { startDate } : {})}
            {...(endDate ? { endDate } : {})}
          />
        </div>
      </SectionErrorBoundary>
    </div>
  )
}
